import { Injectable } from '@angular/core';
import { MediaImageInterface, MediaImageModel } from '@modules/media/models/media-image.model';
import { z } from 'zod';

import { HighlightPageImageStyle } from '../domain/image-styles/highlight-page-image.style';
import { HighlightSectionImageStyle } from '../domain/image-styles/highlight-section-image.style';

import { Adapter } from './adapter';
import { MediaAdapter } from './media.adapter';

const HighlightChapterSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    content_block: z
      .object({
        id: z.string().uuid(),
        title: z.string().nullable(),
        highlight_text: z
          .object({
            processed: z.string(),
          })
          .nullable(),
        image: z.any(), // TODO: use image schema
      })
      .array(),
  })
  .transform((data) => {
    const contentBlocks = data.content_block.map((contentBlock) => {
      let image: any = null;
      if (contentBlock.image) {
        image = {
          id: contentBlock.id,
          title: '',
          caption: '',
          description: '',
          alternate_images: [],
          image: contentBlock.image,
          name: '',
          type: 'files',
          relationships: { image: { meta: contentBlock.image.relationships.meta } },
        };
      }

      return {
        id: contentBlock.id,
        title: contentBlock.title,
        text: contentBlock.highlight_text?.processed,
        image,
      };
    });
    return {
      id: data.id,
      name: data.name,
      contentBlocks,
    };
  });

const HighlightSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string().nullable(),
    chapter_names: z.string().array().nullable(),
    headline: z.string().nullable(),
    teaser_text: z.string().nullable(),
    image: z.any(), // TODO: use image schema
    chapters: HighlightChapterSchema.array().nullable(),
  })
  .transform((data) => {
    let image: any = null;
    if (data.image) {
      image = {
        id: data.image.id,
        title: '',
        caption: '',
        description: '',
        alternate_images: [],
        image: data.image,
        name: '',
        type: 'files',
        relationships: { image: { meta: data.image.relationships.meta } },
      };
    }

    return {
      id: data.id,
      title: data.title,
      image,
      headline: data.headline,
      chapterNames: data.chapter_names,
      chapterLinks: data.chapter_names?.map((chapterName, index) => ({
        label: chapterName,
        routerLink: `../${index}`,
      })),
      teaserText: data.teaser_text,
      chapters: data.chapters,
    };
  });

const HighlightSectionSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string().nullable(),
    highlight_layout: z.enum(['left', 'right']),
    anchor_link: z.string().nullable(),
    highlight: HighlightSchema,
  })
  .transform((data) => ({
    id: data.id,
    type: 'highlight',
    customHeadline: true,
    title: data.title ?? '',
    layout: data.highlight_layout,
    inverse: data.highlight_layout === 'right',
    anchor: data.anchor_link ?? '',
    headline: data.highlight.headline,
    teaserText: data.highlight.teaserText,
    chapterNames: data.highlight.chapterNames,
    chapterLinks: data.highlight.chapterNames?.map((chapterName, index) => ({
      label: chapterName,
      routerLink: `highlight/${data.highlight.id}/${index}`,
    })),
    image: data.highlight.image,
  }));

export type Chapter = z.infer<typeof HighlightChapterSchema>;
export type Highlight = z.infer<typeof HighlightSchema>;
export type HighlightSection = z.infer<typeof HighlightSectionSchema>;

@Injectable({
  providedIn: 'root',
})
export class HighlightAdapter implements Adapter<HighlightSection> {
  constructor(private mediaAdapter: MediaAdapter) {}

  public parse(data: any): HighlightSection {
    const result = HighlightSectionSchema.safeParse(data);
    if (!result.success) {
      console.error('Error parsing Highlight page data', result.error);
      throw result.error;
    } else {
      if (result.data.image) {
        result.data.image = new MediaImageModel(
          this.mediaAdapter.parse(result.data.image, HighlightSectionImageStyle) as MediaImageInterface
        );
      }
      return result.data;
    }
  }

  public parseHighlight(data: any): Highlight {
    const result = HighlightSchema.safeParse(data);
    if (!result.success) {
      console.error('Error parsing Highlight page data', result.error);
      throw result.error;
    } else {
      if (result.data.image) {
        result.data.image = new MediaImageModel(
          this.mediaAdapter.parse(result.data.image, HighlightSectionImageStyle) as MediaImageInterface
        );
      }
      result.data.chapters?.forEach((chapter) => {
        chapter.contentBlocks.forEach((contentBlock) => {
          if (contentBlock.image) {
            contentBlock.image = new MediaImageModel(
              this.mediaAdapter.parseStructuredImage(contentBlock.image, HighlightPageImageStyle, null)
            );
          }
        });
      });
      return result.data;
    }
  }
}
